import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Modal from 'LuxKit/Modal/Modal'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Hr from 'components/Common/Hr'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import { randomIntegerBetween } from 'lib/maths/mathUtils'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalContext from 'contexts/ModalContext'
import LuxPlusFreePreviewModal from 'luxPlus/components/modals/LuxPlusFreePreviewModal'
import useOrderItemName from 'luxPlus/hooks/useOrderItemName'
import usePotentialSavingsRows from 'luxPlus/hooks/usePotentialSavingsRows'
import { ActivityIcon, getItemTypeIcon } from 'luxPlus/lib/itemTypeIcons'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'

interface Props {
  items: Array<App.LuxPlusPotentialSavingsItem>
}

const MobileYearHeader = styled(Pane)`
  padding: ${rem(6)} ${rem(12)};
`

const TabletRow = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: ${rem(32)} minmax(0, 1fr) ${rem(100)} ${rem(100)} ${rem(100)};
  padding: ${rem(8)} ${rem(16)};
  align-items: center;
`

const TabletHeaderRow = styled(TabletRow)`
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.S};
`

// Flight rows mask the amount: the "Sign up to view saving" CTA spans the three price columns.
const MaskedSavingCell = styled.div`
  grid-column: span 3;
  justify-self: end;
`

const TotalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  margin-top: ${rem(16)};
  gap: ${rem(16)};
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.highlight.primary.lightBackground};
`

interface SavingsRowProps {
  item: App.LuxPlusPotentialSavingsItem
  isLast: boolean
  masked: boolean
  unlockPricesText: string
  onSignUp: () => void
}

function SavingsRow({ item, isLast, masked, unlockPricesText, onSignUp }: SavingsRowProps) {
  const intl = useIntl()
  const fallbackLabel = intl.formatMessage({
    id: 'KShouYLLWqCG1vPOfTET',
    defaultMessage: 'Booking',
    description: 'Placeholder label for a past booking in savings table',
  })

  const { name: orderName, itemType, fetching } = useOrderItemName(item.orderId, item.itemId)
  const bookingLabel = orderName || fallbackLabel
  const Icon = getItemTypeIcon(itemType ?? '')

  return <>
    <CSSBreakpoint max="mobile">
      <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="center">
        {fetching && <TextLoadingBox width={`${randomIntegerBetween(10, 18)}ch`} />}
        {!fetching && <BodyText variant="medium" wrap="truncate">{bookingLabel}</BodyText>}
        {masked && <TextButton kind="tertiary" size="small" onClick={onSignUp}>{unlockPricesText}</TextButton>}
        {!masked && <BodyText weight="bold" colour="lux-plus" variant="medium" wrap="no-wrap">
          <FormatCurrency value={item.displayAmount} format="rounded" />
        </BodyText>}
      </Group>
      {!isLast && <Hr size="xs" />}
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <TabletRow>
        <ActivityIcon><Icon /></ActivityIcon>
        <Group direction="horizontal" gap={16} verticalAlign="center">
          {fetching && <TextLoadingBox width={`${randomIntegerBetween(10, 18)}ch`} />}
          {!fetching && <BodyText variant="medium" weight="bold" wrap="truncate">{bookingLabel}</BodyText>}
        </Group>
        {masked && <MaskedSavingCell>
          <TextButton kind="tertiary" size="small" onClick={onSignUp}>{unlockPricesText}</TextButton>
        </MaskedSavingCell>}
        {!masked && <>
          <BodyText align="end" variant="medium">
            <FormatCurrency value={item.displayNonMemberPrice} format="rounded" />
          </BodyText>
          <BodyText align="end" colour="lux-plus" variant="medium">
            <FormatCurrency value={item.displayMemberPrice} format="rounded" />
          </BodyText>
          <BodyText align="end" weight="bold" colour="highlight-primary-dark" variant="medium">
            <FormatCurrency value={item.displayAmount} format="rounded" />
          </BodyText>
        </>}
      </TabletRow>
      {!isLast && <Hr size="xs" />}
    </CSSBreakpoint>
  </>
}

function LuxPlusPotentialSavingsModal({ items }: Props) {
  const intl = useIntl()
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)
  const isFreePreviewMember = useAppSelector(getIsFreePreviewLuxPlusMember)

  const { rows, totalAmount, fetching } = usePotentialSavingsRows(items)

  // Show both exposable rows (with amounts) and flight rows (masked). 'pending' rows are withheld.
  const renderableRows = useMemo(
    () => rows.filter(row => row.status === 'exposable' || row.status === 'flight'),
    [rows],
  )

  const handleBecomeMemberAndClose = useCallback(() => {
    resolve()
    handleBecomeMember()
  }, [resolve, handleBecomeMember])

  const handleStartFreePreview = useCallback(async() => {
    const started = await showModal<boolean>(<LuxPlusFreePreviewModal />)
    if (started) {
      resolve()
    }
  }, [showModal, resolve])

  const rowsByYear = useMemo(() => {
    const sorted = sortBy([...renderableRows], row => new Date(row.item.paymentDate), 'desc')
    const groups = groupBy(sorted, row => new Date(row.item.paymentDate).getFullYear())
    return Array.from(groups.values()).map(group => sortBy(group, row => new Date(row.item.paymentDate), 'desc'))
  }, [renderableRows])

  const youPaidLabel = intl.formatMessage({
    id: 'lRdohPUX8PrHeD06CHUG',
    defaultMessage: 'You paid',
    description: 'Column header for what the non-member paid in savings modal',
  })

  const membersPaidLabel = intl.formatMessage({
    id: 'fTXb92lL5vztbZ3yM0G_',
    defaultMessage: 'Members paid',
    description: 'Column header for what a LuxPlus member would have paid in savings modal',
  })

  const savingsLabel = intl.formatMessage({
    id: 'vmw8FdaNi6qMwdcEjuu0',
    defaultMessage: 'Savings',
    description: 'Column header for potential savings amount in LuxPlus savings modal',
  })

  const unlockPricesText = intl.formatMessage({
    id: 'CP0QmJte26ygG2wBGZDi',
    defaultMessage: 'Unlock member-only flight prices',
    description: 'Per-row CTA shown instead of the amount for a masked flight booking in the potential savings modal',
  })

  const startFreePreviewText = intl.formatMessage({
    id: 'QSgS8MIxsYEieVoMthfX',
    defaultMessage: 'Start free preview',
    description: 'CTA button to start free preview from the LuxPlus savings modal',
  })

  const becomeMemberText = intl.formatMessage({
    id: '0HUIGYLxxAz6_SDbQYrR',
    defaultMessage: 'Become a member',
    description: 'CTA button to become a member from the LuxPlus savings modal',
  })

  return <Modal
    size="L"
    title={
      <Heading variant="heading3">
        {intl.formatMessage({
          id: 'bPlgMRW6qgPoNBMGRU13',
          defaultMessage: 'Potential savings on your past bookings',
          description: 'Title for the potential savings modal showing how much user could have saved',
        })}
      </Heading>
    }
    primaryActionText={becomeMemberText}
    onPrimaryActionClick={handleBecomeMemberAndClose}
    secondaryActionText={isFreePreviewMember ? undefined : startFreePreviewText}
    onSecondaryActionClick={isFreePreviewMember ? undefined : handleStartFreePreview}
  >
    <VerticalSpacer gap={20}>
      {fetching && <TextLoadingBox width="20ch" />}
      {rowsByYear.map(yearRows => <VerticalSpacer gap={8} key={yearRows[0].item.paymentDate}>
        <CSSBreakpoint max="mobile">
          <MobileYearHeader background="light-background">
            <Caption variant="large" weight="bold" colour="light-foreground-alt">
              <FormatDateTime value={yearRows[0].item.paymentDate} year="numeric" />
            </Caption>
          </MobileYearHeader>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <TabletHeaderRow>
            <Caption variant="large" weight="bold" colour="light-foreground-alt" style={{ gridColumn: 'span 2' }}>
              <FormatDateTime value={yearRows[0].item.paymentDate} year="numeric" />
            </Caption>
            <BodyText variant="small" weight="bold" colour="light-foreground-alt" align="end">{youPaidLabel}</BodyText>
            <BodyText variant="small" weight="bold" colour="lux-plus" align="end">{membersPaidLabel}</BodyText>
            <BodyText variant="small" weight="bold" colour="light-foreground-alt" align="end">{savingsLabel}</BodyText>
          </TabletHeaderRow>
        </CSSBreakpoint>
        <Group direction="vertical">
          {yearRows.map((row, idx) => <SavingsRow
            key={row.item.itemId}
            item={row.item}
            isLast={idx === yearRows.length - 1}
            masked={row.status === 'flight'}
            unlockPricesText={unlockPricesText}
            onSignUp={handleBecomeMemberAndClose}
          />)}
        </Group>
      </VerticalSpacer>)}
    </VerticalSpacer>

    {totalAmount > 0 && (
      <TotalRow>
        <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">
          {intl.formatMessage({
            id: 'iNPYzedXhBRi9s4hqkw9',
            defaultMessage: 'How much you could have saved',
            description: 'Label for total potential savings in savings modal',
          })}
        </BodyText>
        <BodyText variant="medium" colour="highlight-primary-dark" weight="bold" align="end">
          <FormatCurrency format="roundedDollar" value={totalAmount} />
        </BodyText>
      </TotalRow>
    )}
  </Modal>
}

export default LuxPlusPotentialSavingsModal
